import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  AdjustInput,
  CalculateInput,
  CommissionAdjustment,
  CalculateResult,
  CommissionRate,
  CommissionRateListParams,
  CommissionRateListResult,
  CommissionRateStats,
  CreateRateInput,
  SettleInput,
  SettlementListParams,
  SettlementListResult,
  SettlementStats,
  SettlementStatsParams,
  UpdateRateInput,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_RATE_LIST: CommissionRateListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_RATE_STATS: CommissionRateStats = { total: 0, active: 0 };
const EMPTY_SETTLEMENT_LIST: SettlementListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_SETTLEMENT_STATS: SettlementStats = { total_payable: 0, already_settled: 0, pending_count: 0 };

export const commissionsApi = {
  rates: (params: CommissionRateListParams) =>
    withFallback(
      () => apiClientPaginated<CommissionRateListResult['data']>(
        `/commissions/rates${buildQuery({ page: params.page, location_id: params.location_id, user_id: params.user_id, is_active: params.is_active, search: params.search })}`,
      ) as Promise<CommissionRateListResult>,
      EMPTY_RATE_LIST,
    ),

  // Tenant-wide rate counts for the hero badges — independent of search/page
  // so "active" reflects ALL active rates, not just the current page (issue #158).
  rateStats: () =>
    withFallback(() => apiClient<CommissionRateStats>('/commissions/rates/stats'), EMPTY_RATE_STATS),

  rate: (id: string) =>
    withFallback(() => apiClient<CommissionRate>(`/commissions/rates/${id}`), null),

  createRate: (input: CreateRateInput) =>
    apiClient<CommissionRate>('/commissions/rates', { method: 'POST', body: input }),

  updateRate: (id: string, input: UpdateRateInput) =>
    apiClient<CommissionRate>(`/commissions/rates/${id}`, { method: 'PATCH', body: input }),

  deactivateRate: (id: string) =>
    apiClient<CommissionRate>(`/commissions/rates/${id}`, { method: 'DELETE' }),

  settlements: (params: SettlementListParams) =>
    withFallback(
      () => apiClientPaginated<SettlementListResult['data']>(
        `/commissions/settlements${buildQuery({
          page: params.page, user_id: params.user_id, location_id: params.location_id,
          status: params.status, start_date: params.start_date, end_date: params.end_date,
        })}`,
      ) as Promise<SettlementListResult>,
      EMPTY_SETTLEMENT_LIST,
    ),

  // Period-scoped KPI totals for the hero cards — independent of the table's
  // status filter and pagination so "Total Payable" / "Pending Count" reflect
  // the whole period, not just the current page (issue #161).
  settlementStats: (params: SettlementStatsParams) =>
    withFallback(
      () => apiClient<SettlementStats>(
        `/commissions/settlements/stats${buildQuery({
          user_id: params.user_id, location_id: params.location_id,
          start_date: params.start_date, end_date: params.end_date,
        })}`,
      ),
      EMPTY_SETTLEMENT_STATS,
    ),

  calculate: (input: CalculateInput) =>
    apiClient<CalculateResult>('/commissions/calculate', { method: 'POST', body: input }),

  settle: (id: string, input: SettleInput) =>
    apiClient<unknown>(`/commissions/settlements/${id}`, { method: 'PATCH', body: input }),

  cancel: (id: string, reason?: string) =>
    apiClient<unknown>(`/commissions/settlements/${id}/cancel`, { method: 'PATCH', body: { reason } }),

  adjust: (id: string, input: AdjustInput) =>
    apiClient<CommissionAdjustment>(`/commissions/settlements/${id}/adjustments`, { method: 'POST', body: input }),

  adjustments: (id: string) =>
    apiClient<CommissionAdjustment[]>(`/commissions/${id}/adjustments`),
};
